import React, { useEffect, useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../actions/authenActions";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

import Logo from "./Logo";
import DashboardFrame from "../pathology/DashboardFrame";
import PatientFrame from "../pathology/PatientFrame";
import ReviewFrame from "../pathology/ReviewFrame";
import SkipFrame from "../pathology/SkipFrame";
import FullSkipFrame from "../pathology/FullSkipFrame";
import ActivityFrame from "../pathology/ActivityFrame";
import PathologyFrame from "../pathology/PathologyFrame";
import ScheduleFrame from "../gp_scheduler/ScheduleFrame";
import MessageFrame from "../pathology/MessageFrame";
import FilingRuleFrame from "../pathology/FilingRuleFrame";
import RuleFrame from "../pathology/RuleFrame";
import DialogFrame from "../pathology/DialogFrame";
import CredentialsFrame from "../pathology/CredentialsFrame";
import SettingsFrame from "../pathology/SettingsFrame";

import S1DashboardFrame from "../s1_pathology/DashboardFrame";
import S1ActivityFrame from "../s1_pathology/ActivityFrame";
import S1PathologyFrame from "../s1_pathology/PathologyFrame";
import S1ScheduleFrame from "../s1_pathology/ScheduleFrame";
import S1MessageFrame from "../s1_pathology/MessageFrame";
import S1FilingRuleFrame from "../s1_pathology/FilingRuleFrame";
import S1RuleFrame from "../s1_pathology/RuleFrame";
import S1DialogFrame from "../s1_pathology/DialogFrame";
import S1CredentialsFrame from "../s1_pathology/CredentialsFrame";
import S1SettingsFrame from "../s1_pathology/SettingsFrame";
import LicenseDetailsFrame from "./LicenseDetailsFrame";
import AboutFrame from "./AboutFrame";
import ReportEmailFrame from "./ReportEmailFrame";
import SecondaryAccountFrame from "./SecondaryAccountFrame";

/* --- COMPONENT MAP --- */
const componentMap = {
  "pathology.dashboard_frame.DashboardFrame": DashboardFrame,
  "pathology.review_frame.ReviewFrame": ReviewFrame,
  "pathology.patient_summary.PatientFrame": PatientFrame,
  "pathology.skip_analysis.SkipFrame": SkipFrame,
  "pathology.skip_analysis.FullSkipFrame": FullSkipFrame,
  "pathology.dashboard_frame.ActivityFrame": ActivityFrame,
  "pathology.run_pathology.PathologyFrame": PathologyFrame,
  "gp_scheduler.schedule_frame_user.ScheduleFrame": ScheduleFrame,
  "pathology.message_frame.MessageFrame": MessageFrame,
  "pathology.filing_rules_frame.FilingRuleFrame": FilingRuleFrame,
  "pathology.rules_frame.RuleFrame": RuleFrame,
  "pathology.dialog_frame.DialogFrame": DialogFrame,
  "pathology.credentials_frame.CredentialsFrame": CredentialsFrame,
  "pathology.settings_frame.SettingsFrame": SettingsFrame,

  "s1_pathology.dashboard_frame.DashboardFrame": S1DashboardFrame,
  "s1_pathology.dashboard_frame.ActivityFrame": S1ActivityFrame,
  "s1_pathology.run_pathology.PathologyFrame": S1PathologyFrame,
  "s1_pathology.schedule_frame_user.ScheduleFrame": S1ScheduleFrame,
  "s1_pathology.message_frame.MessageFrame": S1MessageFrame,
  "s1_pathology.filing_rules_frame.FilingRuleFrame": S1FilingRuleFrame,
  "s1_pathology.rules_frame.RuleFrame": S1RuleFrame,
  "s1_pathology.dialog_frame.DialogFrame": S1DialogFrame,
  "s1_pathology.credentials_frame.CredentialsFrame": S1CredentialsFrame,
  "s1_pathology.settings_frame.SettingsFrame": S1SettingsFrame,
};

const drawerWidth = 240;
const collapsedWidth = 0;

const NotFoundComponent = () => (
  <Typography color="error">Component not found.</Typography>
);

const Dashboard = () => {
  const loggedInUser = useSelector((state) => state.authen.loggedInUser);
  const dispatch = useDispatch();
  const isSecondary = useSelector(
    (state) => state.authen.loggedInUser?.issecondary,
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const mopen = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [navigationItems, setNavigationItems] = useState([]);
  const [open, setOpen] = useState(true);

  /* --- Select first product automatically if none selected --- */
  useEffect(() => {
    if (!loggedInUser?.licensed_products?.length) return;

    if (!selectedProduct) {
      const firstProduct = loggedInUser.licensed_products[0].product_name;
      setSelectedProduct(firstProduct);
      navigate(`/dashboard/${firstProduct.toLowerCase()}`);
    }
  }, [loggedInUser, selectedProduct, navigate]);

  /* --- Update left nav when product changes --- */
  useEffect(() => {
    if (selectedProduct && loggedInUser) {
      const navItems = loggedInUser[selectedProduct];
      setNavigationItems(navItems || []);
    }
  }, [selectedProduct, loggedInUser]);

  const handleProductChange = (e) => {
    const newValue = e.target.value;
    setSelectedProduct(newValue);
    navigate(`/dashboard/${newValue.toLowerCase()}`);
  };

  const handleNavClick = (item) => {
    navigate(`/dashboard/${selectedProduct.toLowerCase()}/${item[0]}`);
  };

  const handleLogout = () => {
    handleClose(); // closes menu

    // Clear storage if you use it
    //localStorage.clear();
    //sessionStorage.clear();

    // Reset Redux
    dispatch(logout());

    // Redirect to login
    navigate("/login");
  };

  /* --- Dynamically render routes from static map --- */
  const renderRoutes = () => {
    if (!navigationItems?.length) return null;

    return navigationItems.map((item) => {
      const routePath = `${selectedProduct.toLowerCase()}/${item[0]}`;
      const Component = componentMap[item[2]] || NotFoundComponent;

      return <Route key={routePath} path={routePath} element={<Component />} />;
    });
  };
  return (
    <Box sx={{ display: "flex" }}>
      {/* Sidebar */}
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: (theme) =>
              theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        {/* Fixed Logo */}
        <Box
          sx={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Logo />{" "}
          {/* Make sure the Logo component scales well in a small space */}
        </Box>

        {/* Optional spacer if needed */}
        <Box sx={{ height: 8 }} />

        {/* Product dropdown (visible only if sidebar open) */}
        {open && (
          <Box sx={{ p: 2, width: "100%" }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                backgroundColor: "#f0f4f5",
                borderRadius: 1,
                ".MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  "& fieldset": {
                    borderColor: "#005EB8",
                  },
                  "&:hover fieldset": {
                    borderColor: "#003087",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#003087",
                  },
                },
              }}
            >
              <InputLabel id="product-select-label">Select Product</InputLabel>
              <Select
                labelId="product-select-label"
                id="product-select"
                value={selectedProduct || ""}
                label="Select Product"
                onChange={handleProductChange}
              >
                {loggedInUser?.licensed_products?.map((product) => (
                  <MenuItem
                    key={product.product_name}
                    value={product.product_name}
                  >
                    {product.product_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Navigation */}
        <List sx={{ width: "100%" }}>
          {navigationItems.map((item) => (
            <ListItem
              button
              key={item[0]}
              onClick={() => handleNavClick(item)}
              selected={location.pathname.endsWith(`/${item[0]}`)}
              sx={{
                justifyContent: open ? "initial" : "center",
                px: 2,
              }}
            >
              {open && <ListItemText primary={item[1]} />}
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: "#f9f9f9", minHeight: "100vh" }}
      >
        <Box
          sx={{
            height: 48,
            bgcolor: "#e3f2fd",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            borderBottom: "1px solid #ddd",
            position: "fixed",
            top: 0,
            left: open ? drawerWidth : collapsedWidth,
            right: 0,
            zIndex: 1200,
            transition: (theme) =>
              theme.transitions.create("left", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setOpen(!open)}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2">
              Welcome, {loggedInUser?.login_account.fullname || "Mr Test User"}
            </Typography>
            <IconButton onClick={handleClick} sx={{ p: 0 }}>
              <AccountCircleIcon fontSize="large" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={mopen}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate("/dashboard/about");
                }}
              >
                About
              </MenuItem>
              <MenuItem
                onClick={() => {
                  window.open("mailto:mybotgpinfo@jifjaff.co.uk", "_blank");
                }}
              >
                Support
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate("/dashboard/license-details");
                }}
              >
                License Details
              </MenuItem>

              {!isSecondary && (
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate("/dashboard/secondary-account");
                  }}
                >
                  Manage Secondary Account
                </MenuItem>
              )}
              {!isSecondary && (
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate("/dashboard/report-emails");
                  }}
                >
                  Report Email Accounts
                </MenuItem>
              )}

              <MenuItem
                onClick={() =>
                  window.open(
                    "/release/free_download/MyBotGP_Pathology_User_Guide.pdf",
                    "_blank",
                  )
                }
              >
                MyBotGP Pathology User Guide
              </MenuItem>
              <MenuItem
                onClick={handleLogout}
                sx={{ color: "error.main", fontWeight: 500 }}
              >
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Spacer below top bar */}
        <Box sx={{ height: 48 }} />

        {/* Sidebar Toggle Button */}

        {/* Main Routes */}
        <Box sx={{ px: 2, pt: 1 }}>
          <Routes>
            {renderRoutes()}
            <Route path="license-details" element={<LicenseDetailsFrame />} />
            <Route path="about" element={<AboutFrame />} />
            <Route path="report-emails" element={<ReportEmailFrame />} />
            <Route
              path="secondary-account"
              element={<SecondaryAccountFrame />}
            />

            <Route
              path="*"
              element={
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="h4" gutterBottom>
                    Welcome to MyBotGP
                  </Typography>
                  <Typography>This is the main content area.</Typography>
                </Paper>
              }
            />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
